import React from 'react'
import { Box, TextField, MenuItem, Grid } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { useTranslation } from 'react-i18next'
import CPTip from '../../../Utils/CPTip'

const DAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA']

function buildRRule(freq, startDate, endDate) {
  if (!freq || !startDate) return ''

  const d = new Date(startDate)
  const day = DAY_CODES[d.getDay()]
  const dayOfMonth = d.getDate()
  const month = d.getMonth() + 1

  let rule = `FREQ=${freq}`

  if (freq === 'WEEKLY') {
    rule += `;BYDAY=${day}`
  } else if (freq === 'MONTHLY') {
    rule += `;BYMONTHDAY=${dayOfMonth}`
  } else if (freq === 'YEARLY') {
    rule += `;BYMONTH=${month};BYMONTHDAY=${dayOfMonth}`
  }

  if (endDate) {
    const until = new Date(endDate)
    until.setUTCHours(23, 59, 59, 0)
    rule += `;UNTIL=${until.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`
  }

  return rule
}

function getFreqLabel(freq, startDate) {
  if (!startDate) return freq

  const d = new Date(startDate)
  const dayName = d.toLocaleDateString('en-US', { weekday: 'long' })
  const dayOfMonth = d.getDate()
  const monthName = d.toLocaleDateString('en-US', { month: 'long' })

  switch (freq) {
    case 'DAILY':   return 'Daily'
    case 'WEEKLY':  return `Weekly on ${dayName}`
    case 'MONTHLY': return `Monthly on the ${dayOfMonth}`
    case 'YEARLY':  return `Annually on ${monthName} ${dayOfMonth}`
    default:        return freq
  }
}

const FREQ_OPTIONS = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY']

export default function DocAlertScheduleForm({ value, onChange }) {
  const { t } = useTranslation()

  const handleChange = (field, val) => {
    onChange({ ...value, [field]: val })
  }

  const handleStartDateChange = (newVal) => {
    const date = newVal?.$d ?? newVal
    const currentFreq = getCurrentFreq(value.recurrence_rule)
    const newRule = buildRRule(currentFreq, date, value.recurrence_end)
    onChange({ ...value, scheduled_at: date, recurrence_rule: newRule })
  }

  const handleEndDateChange = (newVal) => {
    const date = newVal?.$d ?? newVal
    const currentFreq = getCurrentFreq(value.recurrence_rule)
    const newRule = buildRRule(currentFreq, value.scheduled_at, date)
    onChange({ ...value, recurrence_end: date, recurrence_rule: newRule })
  }

  const handleFreqChange = (freq) => {
    const newRule = buildRRule(freq, value.scheduled_at, value.recurrence_end)
    handleChange('recurrence_rule', newRule)
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        {/* Trigger type */}
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            label={t('Trigger type')}
            value={value.trigger_type || ''}
            onChange={(e) => handleChange('trigger_type', e.target.value)}
            size='small'
          >
            <MenuItem value='before_expiry'>{t('Before document expiry')}</MenuItem>
            <MenuItem value='fixed_date'>{t('On a specific date')}</MenuItem>
            <MenuItem value='recurring'>{t('Recurring')}</MenuItem>
          </TextField>
        </Grid>

        {/* before_expiry */}
        {value.trigger_type === 'before_expiry' && (
          <Grid item xs={12}>
            <CPTip text={t('The alert will fire N days before the document expires')} />
            <TextField
              fullWidth
              type='number'
              label={t('Days before expiry')}
              value={value.offset_days || ''}
              onChange={(e) => handleChange('offset_days', parseInt(e.target.value))}
              inputProps={{ min: 1 }}
              size='small'
            />
          </Grid>
        )}

        {/* fixed_date */}
        {value.trigger_type === 'fixed_date' && (
          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                label={t('Date')}
                value={value.scheduled_at || null}
                onChange={(newVal) => handleChange('scheduled_at', newVal?.$d ?? newVal)}
                renderInput={(params) => <TextField {...params} fullWidth size='small' />}
              />
            </LocalizationProvider>
          </Grid>
        )}

        {/* recurring */}
        {value.trigger_type === 'recurring' && (
          <>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  label={t('Start date')}
                  value={value.scheduled_at || null}
                  onChange={handleStartDateChange}
                  renderInput={(params) => <TextField {...params} fullWidth size='small' />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  label={t('End date (optional)')}
                  value={value.recurrence_end || null}
                  onChange={handleEndDateChange}
                  minDate={value.scheduled_at || undefined}
                  renderInput={(params) => <TextField {...params} fullWidth size='small' />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label={t('Recurrence pattern')}
                value={getCurrentFreq(value.recurrence_rule)}
                onChange={(e) => handleFreqChange(e.target.value)}
                size='small'
              >
                {FREQ_OPTIONS.map((freq) => (
                  <MenuItem key={freq} value={freq}>
                    {t(getFreqLabel(freq, value.scheduled_at))}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  )
}

// Helper: extract FREQ=XXX from an rrule string
function getCurrentFreq(rrule) {
  if (!rrule) return ''
  const match = rrule.match(/FREQ=([A-Z]+)/)
  return match ? match[1] : ''
}